/**
 * Supplies API: the supplies taxonomy, faceted search over every crawled supply, one supply with
 * the evidence of each spec, corrections by hand and the shopping list, under /api/supplies/
 * (backend/supplies/api.py). Money is integer minor units of the workspace currency; unit prices
 * are decimal strings in minor units per piece, litre or kg (`unit_basis`). Query keys start with
 * "supplies".
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/api/client";
import type { AssetCopy, DecimalString, IsoDateTime, MinorUnits, Uuid } from "@/api/types";

export type FacetKind = "number" | "enum" | "boolean";
export type SpecValue = string | boolean;
/** What a unit price is per: a piece, or a litre or kilogram of what is sold by volume or mass. */
export type UnitBasis = "piece" | "litre" | "kg";

export interface FacetValueDef {
  value: string;
  label: string;
}

export interface FacetDef {
  key: string;
  label: string;
  kind: FacetKind;
  unit: string;
  help: string;
  /** Empty for open enums (neck finishes, brands): their values come from the data. */
  values: FacetValueDef[];
}

export interface SupplyCategory {
  slug: string;
  label: string;
  description: string;
  unit_basis: UnitBasis;
  /** A group of categories searched together ("Vials and bottles"): never one a listing is filed under. */
  includes: string[];
  subcategories: FacetValueDef[];
  facets: FacetDef[];
}

export interface NumberCondition {
  min?: string;
  max?: string;
}

export interface ValuesCondition {
  values: string[];
}

export type FacetCondition = NumberCondition | ValuesCondition;

export interface FacetCount {
  key: string;
  label: string;
  kind: FacetKind;
  unit: string;
  help: string;
  /** Items (matching every other filter) that state this facet, themselves or through an add-on. */
  known: number;
  /** Items that don't state it: left out by a filter on it unless `include_unknown`. */
  unknown: number;
  /** Items it doesn't apply to (the plastic of a glass bottle): neither known nor unknown. */
  not_applicable: number;
  /** `add_on_count`: the items that have the value only through an add-on. */
  values: { value: string; label: string; count: number; add_on_count: number }[];
  min: DecimalString | null;
  max: DecimalString | null;
  selected: FacetCondition | null;
  include_unknown: boolean;
}

export interface SupplyOffer {
  price_minor: MinorUnits | null;
  currency: string;
  listed_price_minor: MinorUnits | null;
  listed_currency: string;
  in_stock: boolean | null;
  observed_at: IsoDateTime | null;
  /** The market (ISO country) the store priced it for. */
  market: string;
  /** The store shows no price for the workspace's market, so this one is for `market`. */
  other_market: boolean;
  /** How the price was converted to the workspace currency. */
  note: string;
}

export interface SupplierRef {
  id: Uuid;
  slug: string;
  name: string;
  country: string;
  ships_to_workspace?: boolean | null;
}

/** A unit price far off what comparable supplies cost, which usually means a wrong pack size. */
export interface PriceCheck {
  /** "low": far cheaper than comparable supplies (a pack read too big); "high": far dearer (a case read as one). */
  direction: "low" | "high";
  /** The unit price over the typical one. */
  ratio: DecimalString;
  unit_minor: DecimalString;
  typical_minor: DecimalString;
  currency: string;
  basis: UnitBasis;
  compared_with: number;
  /** The supplies it was compared with: "bottles: glass, 8–16 ml". */
  group: string;
}

export interface SupplyRow {
  id: Uuid;
  category: string;
  subcategory: string;
  title: string;
  variant_title: string;
  sku: string;
  url: string;
  product_id: Uuid;
  variant_id: Uuid;
  supplier: SupplierRef;
  specs: Record<string, SpecValue>;
  unresolved: string[];
  /** Facet values only the store's add-ons offer (a cap it can be ordered with): {facet: [values]}. */
  add_on_values: Record<string, string[]>;
  /** Facets that don't apply to it (the plastic of a glass bottle). */
  not_applicable: string[];
  /** Its unit price looks off; null when it looks right or was marked right. */
  price_check: PriceCheck | null;
  pack_count: number | null;
  capacity_ml: DecimalString | null;
  offer: SupplyOffer;
  /** Price per `unit_basis` in minor units of the workspace currency; null when the pack doesn't say how much it holds. */
  unit_price_minor: DecimalString | null;
  unit_basis: UnitBasis;
  /** US-made glass shipped into Canada from abroad: 50% surtax at the border. */
  us_glass_surtax: boolean;
  notes: string[];
  in_list: boolean;
}

export interface InterpretedQuery {
  category: string;
  filters: Record<string, FacetCondition>;
  terms: { facet: string; label: string; text: string }[];
  rest: string;
}

export interface CategoryCount {
  slug: string;
  label: string;
  description: string;
  count: number;
}

export interface UnitPriceRange {
  basis: UnitBasis;
  /** Offers with a unit price in this basis. */
  priced: number;
  min_minor: DecimalString;
  median_minor: DecimalString;
  max_minor: DecimalString;
}

export interface OverviewSupplier {
  supplier: SupplierRef;
  /** In the search's supplier filter. */
  selected: boolean;
  count: number;
  in_stock: number;
  lowest_price_minor: MinorUnits | null;
  lowest_unit_prices: { basis: UnitBasis; minor: DecimalString }[];
}

/** Who sells what the search found, counted without the supplier filter. */
export interface MarketOverview {
  /** One per unit basis, the most common first. */
  unit_price_ranges: UnitPriceRange[];
  /** Cheapest per unit first. */
  suppliers: OverviewSupplier[];
}

export interface SupplySearch {
  category: string;
  count: number;
  items: SupplyRow[];
  /** The sort used: a price sort falls back to "title" when too many supplies match (see `notes`). */
  sort: string;
  notes: string[];
  facets: FacetCount[];
  categories: CategoryCount[];
  /** With a category, unless too many supplies match. */
  overview: MarketOverview | null;
  /** The suppliers the search is limited to. */
  supplier_filter: { slug: string; name: string }[];
  interpreted: InterpretedQuery | null;
  currency: string;
  /** How many supplies of the search, the "Prices to check" filter aside, have a price check. */
  price_check_count: number;
}

export interface SpecDetail {
  key: string;
  label: string;
  kind: FacetKind;
  unit: string;
  value: SpecValue | null;
  value_label: string | null;
  /** Where the value was read: variant_specs, variant_options, variant_title, product_specs, product_title, description, document, variant_pack, implied or manual. */
  source: string;
  text: string;
  url: string;
  /** Our copy of the document at `url`, when it is a saved spec sheet; "" otherwise. */
  copy_url: string;
  manual: boolean;
  unresolved_values: string;
  unresolved_text: string;
  /** False when another spec rules this one out (no cap liner on a bottle sold without a cap). */
  applies: boolean;
  /** Values only its add-ons offer. */
  add_on_values: FacetValueDef[];
}

export interface AddOnChoice {
  label: string;
  /** The supply item of the store's own listing for this choice, when we have it. */
  item_id: Uuid | null;
  specs: { key: string; label: string; value_label: string }[];
}

export interface SupplyDetail extends SupplyRow {
  description: string;
  /** Opens the store's cart with one pack in it; empty when the store has no cart links. */
  one_pack_cart_url: string;
  specs_detail: SpecDetail[];
  /** What the store offers to order with it ("Add CT Closure"), the choices that name a closure. */
  add_ons: { name: string; choices: AddOnChoice[] }[];
  listing: {
    id: Uuid;
    category: string;
    subcategory: string;
    origin: "store_category" | "title" | "manual";
    reason: string;
    confidence: DecimalString | null;
    category_path: string;
    excluded: boolean;
    /** The spec sheets and SDSs it was read from, with our copy of each when one was saved. */
    documents: { url: string; kind: string; fetched_at: string; copy?: AssetCopy | null }[];
  };
  siblings: SupplyRow[];
}

export interface ShoppingListLine {
  id: Uuid;
  variant_id: Uuid;
  item_id: Uuid | null;
  title: string;
  variant_title: string;
  sku: string;
  url: string;
  quantity: number;
  note: string;
  pack_count: number | null;
  offer: SupplyOffer;
  total_minor: MinorUnits | null;
}

export interface ShoppingListGroup {
  supplier: SupplierRef;
  lines: ShoppingListLine[];
  subtotal_minor: MinorUnits;
  /** Null when the supplier publishes no rate to your country and you have no shipping rule. */
  shipping_minor: MinorUnits | null;
  shipping_notes: string[];
  free_threshold_minor: MinorUnits | null;
  import_charges: {
    total_minor: MinorUnits;
    duty_minor: MinorUnits;
    gst_hst_minor: MinorUnits;
    pst_minor: MinorUnits;
    notes: string[];
  } | null;
  us_glass_surtax_minor: MinorUnits;
  total_minor: MinorUnits;
  prices_missing: number;
  handoff: { urls: string[]; note: string; multi_item: boolean };
  order_text: string;
}

export interface ShoppingList {
  currency: string;
  groups: ShoppingListGroup[];
  total_minor: MinorUnits;
  lines: number;
}

/** Query parameters of /api/supplies/search, as kept in the page URL. */
export type SearchQuery = Record<string, string>;

export function useSupplyCategories() {
  return useQuery({
    queryKey: ["supplies", "categories"],
    queryFn: ({ signal }) => apiGet<SupplyCategory[]>("/supplies/categories", { signal }),
    staleTime: Infinity,
  });
}

export function useSupplySearch(query: SearchQuery) {
  return useQuery({
    queryKey: ["supplies", "search", query],
    queryFn: ({ signal }) => apiGet<SupplySearch>("/supplies/search", { params: query, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useSupplyItem(id: Uuid | null) {
  return useQuery({
    queryKey: ["supplies", "item", id],
    queryFn: ({ signal }) => apiGet<SupplyDetail>(`/supplies/items/${id}`, { signal }),
    enabled: id !== null,
  });
}

export function useSetItemSpecs(id: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (specs: Record<string, SpecValue | null>) => apiPatch<SupplyDetail>(`/supplies/items/${id}`, { specs }),
    onSuccess: (detail) => {
      client.setQueryData(["supplies", "item", id], detail);
      void client.invalidateQueries({ queryKey: ["supplies", "search"] });
    },
  });
}

/** "Looks right": clear a supply's price check until its price or the amount in a pack changes. */
export function useDismissPriceCheck(id: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<SupplyDetail>(`/supplies/items/${id}/price-check/dismiss`),
    onSuccess: (detail) => {
      client.setQueryData(["supplies", "item", id], detail);
      void client.invalidateQueries({ queryKey: ["supplies", "search"] });
    },
  });
}

export function useUpdateListing() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: Uuid; category?: string; excluded?: boolean }) =>
      apiPatch<{ id: Uuid; category: string; excluded: boolean }>(`/supplies/listings/${id}`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["supplies"] }),
  });
}

export function useShoppingList() {
  return useQuery({
    queryKey: ["supplies", "list"],
    queryFn: ({ signal }) => apiGet<ShoppingList>("/supplies/list", { signal }),
  });
}

export function useAddToList() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { variant_id: Uuid; quantity: number; note?: string }) =>
      apiPost<{ id: Uuid; variant_id: Uuid; quantity: number }>("/supplies/list", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["supplies", "list"] });
      void client.invalidateQueries({ queryKey: ["supplies", "search"] });
    },
  });
}

export function useUpdateListLine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: Uuid; quantity?: number; note?: string }) =>
      apiPatch<{ id: Uuid }>(`/supplies/list/${id}`, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["supplies", "list"] }),
  });
}

export function useRemoveListLine() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiDelete<null>(`/supplies/list/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["supplies", "list"] });
      void client.invalidateQueries({ queryKey: ["supplies", "search"] });
    },
  });
}
